import {Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {pluralize} from 'common/util/string';

import template from './index.jade';
import css from './index.styl?inline';
import './calendar-input';

const CUSTOM_RANGE_TYPES = {
  BETWEEN: `between`,
  ON: `on`,
  RELATIVE: `in the last`,
  RELATIVE_FUTURE: `in the next`,
  RELATIVE_BEFORE_IN_PAST: `more than`,
  SINCE: `since`,
};

const UNITS = {
  HOUR: `hour`,
  DAY: `day`,
  WEEK: `week`,
  MONTH: `month`,
};

export class CohortsDateRangePicker extends Component {
  static get observedAttributes() {
    return Component.observedAttributes.concat([`custom-range-type`, `from`, `preposition`, `to`, `unit`, `value`]);
  }
  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(...arguments);
    switch (attr) {
      case `custom-range-type`:
        this.update({customRangeType: newValue});
        requestAnimationFrame(() => this.dispatchEvent(new CustomEvent(`draw`, {bubbles: true, composed: true})));
        break;
      case `from`:
        this.update({fromDate: newValue});
        break;
      case `to`:
        this.update({toDate: newValue});
        break;
      case `unit`:
        this.update({unit: Object.values(UNITS).includes(newValue) ? newValue : UNITS.DAY});
        break;
      case `value`:
        this.update({value: newValue});
        break;
    }
  }

  get config() {
    return {
      ...super.config,
      useShadowDom: true,
      css,
      template,
      defaultState: {
        customRangeType: null,
        fromDate: null,
        toDate: null,
        unit: null,
        value: ``,
      },
      helpers: {
        ...super.config.helpers,
        CUSTOM_RANGE_TYPES,
        UNITS,
        pluralize: (string, value = 2) => pluralize(string, value),
        changedDates: (ev) => {
          let {from, to} = ev.detail;
          if (this.state.customRangeType === CUSTOM_RANGE_TYPES.ON) {
            to = from;
          } else if (this.state.customRangeType === CUSTOM_RANGE_TYPES.SINCE) {
            to = null;
          }
          this.dispatchChangeEvent({from, to});
        },
        changedRelativeRangeValue: (ev) => {
          this.dispatchChangeEvent({value: parseInt(ev.target.value, 10)});
        },
        changedRelativeRangeUnit: (ev) => {
          const unit = Object.values(UNITS).find((unit) => this.helpers.pluralize(unit) === ev.detail.value);
          this.dispatchChangeEvent({unit});
          this.selectRelativeInput();
        },
        handleInsertCalendarInput: (vnode) => {
          switch (this.state.customRangeType) {
            case CUSTOM_RANGE_TYPES.ON:
            case CUSTOM_RANGE_TYPES.SINCE:
              requestAnimationFrame(() => vnode.elm.focusFromInput());
              break;
            default:
              break;
          }
        },
      },
    };
  }

  focusRelativeInput() {
    this.relativeRangeInput.focus();
  }

  selectRelativeInput() {
    this.relativeRangeInput.select();
  }

  focusFromInput() {
    if (this.initialized) {
      this.calendarInput.focusFromInput();
    }
  }

  selectFromInput() {
    if (this.initialized) {
      this.calendarInput.selectFromInput();
    }
  }

  focusToInput() {
    if (this.initialized) {
      this.calendarInput.focusToInput();
    }
  }

  selectToInput() {
    if (this.initialized) {
      this.calendarInput.selectToInput();
    }
  }

  dispatchChangeEvent(detail) {
    this.dispatchEvent(new CustomEvent(`change`, {detail}));
  }

  get calendarInput() {
    return this.el.querySelector(`cohorts-calendar-input`);
  }

  get relativeRangeInput() {
    return this.el.querySelector(`input.relative-range-value`);
  }
}

export const CohortsDateRangePickerEl = defineAndMakeJsxFactory(`cohorts-date-range-picker`, CohortsDateRangePicker);
